import { useEffect, useState, useCallback, useRef } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { AlertQuantumRoot } from "./components/quantum/AlertQuantum";
import CountryCode from "./CountryCode";
import CurrentSubscription from "./CurrentSubscription";
import i18n from "./i18n";
import PushAlarms from "./PushAlarms";
import exporterPrivateRoutes from "./routes/exporter/private";
import importerPrivateRoutes from "./routes/importer/private";
import privateRoutes from "./routes/private/private";
import publicRoutes from "./routes/public/public";
import { useAppSelector } from "./store";
import GlobalStyle from "./styles/GlobalStyle";
import { isDevelopment } from "./utils/environment";
import { isNull } from "./utils/is";
import useContentLoading from "./hooks/useContentLoading";
import { useDeferredInit } from "./hooks/useDeferredInit";
import { useI18nPreload } from "./hooks/useI18nPreload";
import { useNormalizedUser } from "./hooks/useNormalizedUser";
import { SendBirdProvider } from "@sendbird/uikit-react";
import "@sendbird/uikit-react/dist/index.css";
import FloatingChat from "./components/organism/FloatingChat";
import SendBirdUnreadCountProvider from "./components/organism/FloatingChat/SendBirdUnreadCountProvider";
import Loader from "./components/atom/Loader";
import {
  useInitSendbirdUser,
  getCachedUser,
  setCachedUser,
  SendbirdUser,
  tokenManager,
  useGetSendbirdTokenMutation,
} from "./store/apis/sendbird";
import { clearChatStateFromSession } from "./utils/chatSessionStorage";

export let analytics: any = null;

const DOCUMENT_TITLE_DEVELOP = "[Dev] ECOYA Export";
const router = createBrowserRouter([
  ...importerPrivateRoutes,
  ...exporterPrivateRoutes,
  ...privateRoutes,
  ...publicRoutes,
]);

function App() {
  const user = useAppSelector((state) => state.auth.user);
  const normalizedUser = useNormalizedUser(user);

  const [isReady, setIsReady] = useState(false);
  const [sendbirdUser, setSendbirdUser] = useState<SendbirdUser | null>(null);
  const [sendbirdLoading, setSendbirdLoading] = useState(false);
  const [initializationAttempted, setInitializationAttempted] = useState(false);
  const [sendbirdAccessToken, setSendbirdAccessToken] = useState<string | null>(
    null,
  );
  const [retryCount, setRetryCount] = useState(0);
  const [initError, setInitError] = useState<string | null>(null);

  const { initializeUser: initializeSendbirdUserApi } = useInitSendbirdUser();
  const [getSendbirdToken] = useGetSendbirdTokenMutation();

  // Refs for cleanup
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const MAX_RETRIES = 3;

  // 채팅 가용성 확인 - defer initial check to reduce mount time

  useContentLoading();

  // Preload i18n namespaces based on route
  useI18nPreload();

  // Memoize initializeUser to avoid unnecessary re-renders
  const initializeUser = useCallback(initializeSendbirdUserApi, [
    initializeSendbirdUserApi,
  ]);

  // 개발환경 dev, local시 document Title, favicon 변경
  useEffect(() => {
    if (isDevelopment) {
      document.title = DOCUMENT_TITLE_DEVELOP;

      const favicon = document.querySelector("link[rel='icon']");
      if (favicon instanceof HTMLLinkElement) {
        favicon.href = isDevelopment ? "/favicon-dev.ico" : "/favicon.ico";
      }
    }

    // Clear chat session state on app initialization (page refresh/restart)
    // This ensures chat always opens to channel list on fresh page loads
    clearChatStateFromSession();

    // Cleanup on unmount
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // 이메일 공유 페이지, 로그인 하지 않은 유저는 라이브러리 언어 (개인정보 언어에 따르지 않음)
  // 라이브러리 언어값은 항상 유저의 언어값으로 동기화
  useEffect(() => {
    const isSharedEmailPage = window.location.pathname.includes("shared-email");

    if (isSharedEmailPage || isNull(user)) {
      return;
    }

    const userLanguage = normalizedUser?.language || i18n.language;

    if (userLanguage && userLanguage !== i18n.language) {
      void i18n.changeLanguage(userLanguage);
    }
  }, [user]);

  // Wait for i18n to be properly initialized with simpler approach
  useEffect(() => {
    const checkTranslationsReady = () => {
      // Check if i18n is initialized and has critical namespaces loaded
      const currentLang = i18n.language || "ko";

      // Must have all critical namespaces loaded
      const criticalNamespaces = [
        "common",
        "error",
        "signup",
        "alert",
        "placeholder",
      ];
      return (
        i18n.isInitialized &&
        criticalNamespaces.every((ns) =>
          i18n.hasResourceBundle(currentLang, ns),
        )
      );
    };

    const onI18nChange = () => {
      if (checkTranslationsReady()) {
        setIsReady(true);
        i18n.off("initialized", onI18nChange);
        i18n.off("loaded", onI18nChange);
        i18n.off("languageChanged", onI18nChange);
      }
    };

    // Check immediately
    if (checkTranslationsReady()) {
      setIsReady(true);
      return;
    }

    // Listen for i18n events
    i18n.on("initialized", onI18nChange);
    i18n.on("loaded", onI18nChange);
    i18n.on("languageChanged", onI18nChange);

    // Fallback timeout - don't wait forever
    const fallbackTimeout = setTimeout(() => {
      setIsReady(true);
    }, 500); // Even quicker fallback

    // Cleanup
    return () => {
      i18n.off("initialized", onI18nChange);
      i18n.off("loaded", onI18nChange);
      i18n.off("languageChanged", onI18nChange);
      clearTimeout(fallbackTimeout);
    };
  }, []);

  // Load remaining i18n namespaces - non-blocking
  useDeferredInit(
    async () => {
      const { loadRemainingNamespaces } = await import("./i18n");
      await loadRemainingNamespaces();
    },
    [],
    { priority: "idle" },
  );

  // Initialize Firebase - non-blocking
  useDeferredInit(
    async () => {
      if (!isDevelopment) {
        const { initializeApp } = await import("firebase/app");
        const { getAnalytics } = await import("firebase/analytics");
        const { FIREBASE_CONFIG } = await import("./constant/firebaseConfig");

        const app = initializeApp(FIREBASE_CONFIG);
        analytics = getAnalytics(app);
      }
    },
    [isDevelopment],
    { priority: "idle" },
  );

  // Register i18n Service Worker for aggressive caching - non-blocking
  useDeferredInit(
    async () => {
      const { registerI18nServiceWorker, warmupI18nCache } = await import(
        "./utils/i18nServiceWorker"
      );

      await registerI18nServiceWorker();

      // Warm up cache with all critical translations for current language first
      const currentLang = i18n.language || "ko";
      await warmupI18nCache(
        [currentLang],
        ["common", "error", "signup", "alert", "placeholder"],
      );

      // Then warm up other languages in background
      const otherLangs = ["en", "ko", "ja", "zh-CN", "zh-TW"].filter(
        (lang) => lang !== currentLang,
      );
      setTimeout(() => {
        warmupI18nCache(otherLangs, [
          "common",
          "error",
          "signup",
          "alert",
          "placeholder",
        ]);
      }, 2000);
    },
    [],
    { priority: "idle" },
  );

  // Sendbird 사용자 초기화 - Sequential initialization to prevent race conditions
  useDeferredInit(
    async () => {
      // 이미 초기화를 시도했거나 진행 중이면 스킵
      if (initializationAttempted || sendbirdLoading) {
        return;
      }

      // 로그인하지 않았거나 비활성화 계정인 경우 Sendbird 초기화 스킵
      if (!normalizedUser?.accountId || !normalizedUser.isActivated) {
        setSendbirdUser(null);
        setSendbirdAccessToken(null);
        tokenManager.clearToken(); // 로그아웃 시 토큰 클리어
        return;
      }

      const userId = String(normalizedUser.accountId);
      const nickname = normalizedUser.email || userId;

      // 캐시 확인
      const cachedUser = getCachedUser(userId);
      if (cachedUser && sendbirdAccessToken) {
        setSendbirdUser(cachedUser);
        setInitializationAttempted(true);
        return;
      }

      // 사용자 초기화 - Sequential approach to prevent race condition
      setSendbirdLoading(true);
      setInitializationAttempted(true);
      setInitError(null);

      // Create new AbortController for this initialization
      abortControllerRef.current = new AbortController();

      try {
        // Step 1: Get SendBird token first (must complete before user initialization)
        const tokenResult = await getSendbirdToken();

        let token: string | null = null;
        if ("data" in tokenResult && tokenResult.data) {
          token = tokenResult.data.sendbird_token;
          setSendbirdAccessToken(token);

          // 토큰을 tokenManager에도 저장
          await tokenManager.getToken();
        }

        // Step 2: Only initialize user after token is successfully obtained
        if (token) {
          const metadata = {
            email: normalizedUser.email || "",
            companyName: normalizedUser.companyName || undefined,
            timezone: "Asia/Seoul",
            role: normalizedUser.role || undefined,
          };

          // Step 3: Initialize user with the obtained token
          const initializedUser = await initializeUser(
            userId,
            nickname,
            "",
            metadata,
          );

          if (initializedUser) {
            setSendbirdUser(initializedUser);
            setCachedUser(userId, initializedUser);
            setRetryCount(0); // Reset retry count on success
            setInitError(null);
          }
        } else {
          console.warn(
            "Failed to obtain SendBird token, skipping user initialization",
          );
          setInitError("Failed to obtain chat token");
        }
      } catch (error: any) {
        // Ignore abort errors
        if (error?.name === "AbortError") {
          return;
        }

        console.error("Failed to initialize Sendbird user:", error);
        setInitError(error?.message || "Failed to initialize chat");

        // Reset states on error to allow retry
        setSendbirdAccessToken(null);
        setSendbirdUser(null);

        // 재시도 횟수 체크 후 재시도
        if (retryCount < MAX_RETRIES) {
          // Exponential backoff: 3초, 6초, 12초
          const retryDelay = 3000 * Math.pow(2, retryCount);

          // Clear existing timeout if any
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
          }

          retryTimeoutRef.current = setTimeout(() => {
            setInitializationAttempted(false);
            setRetryCount((prev) => prev + 1);
          }, retryDelay);
        } else {
          console.error(
            `Max retries (${MAX_RETRIES}) reached for Sendbird initialization`,
          );
        }
      } finally {
        setSendbirdLoading(false);
      }
    },
    [
      user,
      initializationAttempted,
      sendbirdLoading,
      getSendbirdToken,
      sendbirdAccessToken,
      initializeUser,
    ],
    { delay: 500, priority: "idle" }, // Use idle priority for better performance
  );

  // 사용자가 로그아웃하면 초기화 상태 리셋
  useEffect(() => {
    if (!normalizedUser?.accountId && initializationAttempted) {
      // Cancel any ongoing initialization
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }

      setInitializationAttempted(false);
      setSendbirdUser(null);
      setSendbirdAccessToken(null);
      setRetryCount(0);
      setInitError(null);
      tokenManager.clearToken();
    }
  }, [user, initializationAttempted]);

  // 로그인한 유저가 있는데 토큰이 없거나 초기화가 안된 경우 재시도 (재시도 횟수 제한)
  useEffect(() => {
    if (
      normalizedUser?.accountId &&
      !sendbirdAccessToken &&
      !sendbirdLoading &&
      !initializationAttempted &&
      retryCount < MAX_RETRIES
    ) {
      // 로그인 후 초기화가 필요한 경우 재초기화 트리거
      // (initializationAttempted가 false이므로 useDeferredInit이 다시 실행됨)
    }
  }, [
    user,
    sendbirdAccessToken,
    sendbirdLoading,
    initializationAttempted,
    retryCount,
  ]);

  // Wait for critical i18n resources before rendering

  if (!isReady) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100vw",
        }}
      >
        <Loader size={40} />
      </div>
    );
  }

  return (
    <AlertQuantumRoot>
      {/* Sendbird 초기화 - 실제 사용자 정보와 토큰 사용 */}
      <SendBirdProvider
        appId={process.env.REACT_APP_SENDBIRD_ID || ""}
        userId={
          sendbirdUser?.user_id ||
          (normalizedUser ? String(normalizedUser.accountId) : "guest")
        }
        accessToken={sendbirdAccessToken || undefined}
        nickname={
          normalizedUser
            ? normalizedUser.isExporterUser
              ? `${normalizedUser.name} (${normalizedUser.companyName})`
              : `${normalizedUser.name || "User"}`
            : "Guest"
        }
        theme="light"
        colorSet={{
          "--sendbird-light-primary-400": "#3A7FC8", // primaryDark
          "--sendbird-light-primary-300": "#166DD7", // primary (blue4)
          "--sendbird-light-primary-100": "#E1F0FF", // primaryLight
        }}
      >
        <GlobalStyle />
        <CountryCode />
        <PushAlarms />
        <CurrentSubscription />
        <RouterProvider router={router} />
        {/* FloatingChat은 로그인한 활성화 사용자에게만 표시 */}
        {/* 채팅 버튼은 로그인 후 즉시 표시되고, Sendbird 초기화는 백그라운드에서 진행 */}
        {normalizedUser?.accountId && normalizedUser.isActivated ? (
          <SendBirdUnreadCountProvider>
            <FloatingChat
              isLoading={sendbirdLoading}
              error={initError}
              retryCount={retryCount}
              maxRetries={MAX_RETRIES}
              onRetry={() => {
                setInitializationAttempted(false);
                setRetryCount(0);
                setInitError(null);
              }}
            />
          </SendBirdUnreadCountProvider>
        ) : null}
      </SendBirdProvider>
    </AlertQuantumRoot>
  );
}

export default App;
